import './agenda-available-block-list-item.scss';

import { first } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import {
  AgendaAvailableItemDataFragment,
  AgendaAvailableSlotItemDataFragment,
  BaseHealthProfessionalItemFragment,
  ConsultType,
} from '../../../data/types/api/graphql';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedDate } from '../../../elements/intl/custom-formatted-date';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { AgendaAvailableAppointmentSlotList } from './agenda-available-appointment-slot-list';

type AgendaAvailableBlockListItemProps = {
  active?: boolean;
  agendaBlockItem: AgendaAvailableItemDataFragment;
  consultType: ConsultType;
  showAsRange?: boolean;
  compact?: boolean;
  multiple?: boolean;
  value?: {
    healthProfessionalId: string | null | undefined;
    selectedSlots: AgendaAvailableSlotItemDataFragment[];
  };

  onActive?: (value: string) => void;
  onClick?: (
    healthProfessional: BaseHealthProfessionalItemFragment,
    slots: AgendaAvailableSlotItemDataFragment[]
  ) => void;
};

export function AgendaAvailableBlockListItem({
  agendaBlockItem,
  showAsRange,
  // values,
  consultType,
  value,
  compact,
  multiple,
  onActive,
  onClick,
  ...props
}: AgendaAvailableBlockListItemProps) {
  const [active, setActive] = useState<boolean>(false);

  useEffect(() => {
    if (props.active) {
      setActive(props.active);
    }
  }, [props.active]);

  const onHandleActive = useCallback(() => {
    setActive(prevState => {
      const newState = !prevState;

      onActive?.(newState ? agendaBlockItem.healthProfessional.id : '');

      return newState;
    });
  }, [agendaBlockItem, onActive]);

  const { getFullName } = useFormatters();

  return (
    <div
      className={`agenda-appointment-block-list-item${active ? ' active' : ''}`}
    >
      <div
        className="agenda-appointment-block-list-item__header"
        role="button"
        tabIndex={0}
        onClick={onHandleActive}
        onKeyDown={makeKeyDownHandler(onHandleActive)}
      >
        <h2>
          {getFullName(agendaBlockItem.healthProfessional)}
          <small>
            (
            <CustomFormattedMessage
              id="agenda.appointment.first-available"
              values={{
                time: (
                  <CustomFormattedDate
                    value={first(agendaBlockItem.slots)?.startTime}
                    showTime
                    weekday="long"
                  />
                ),
              }}
            />
            )
          </small>
        </h2>
      </div>

      {active && (
        <div className="agenda-appointment-block-list-item__content">
          <AgendaAvailableAppointmentSlotList
            consultType={consultType}
            healthProfessionalId={agendaBlockItem.healthProfessional.id}
            multiple={multiple}
            showAsRange={showAsRange}
            compact={compact}
            items={agendaBlockItem.slots}
            value={value}
            onClick={
              onClick
                ? slots => {
                    onClick?.(agendaBlockItem.healthProfessional, slots);
                  }
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
